/**
 * Wraps a promise with a timeout mechanism to prevent indefinite waiting.
 *
 * This utility races the provided promise against a timeout promise. If the original
 * promise doesn't resolve/reject within the specified timeout period, it will be
 * rejected with a timeout error.
 *
 * @example
 * ```typescript
 * const data = await queryWithTimeout(
 *   fetchUserData(),
 *   (error) => console.error('Request timed out:', error),
 *   3000
 * );
 * ```
 */
export const queryWithTimeout = async <T>(
  promise: Promise<T>,
  onTimeout?: (error: Error) => void,
  timeoutInMillis = 10_000 // 10 seconds default timeout for now
): Promise<T> => {
  let timeoutHandle: NodeJS.Timeout;

  // Create a promise that rejects after the specified timeout
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      const error = new Error('Query timed out');
      onTimeout?.(error);
      reject(error);
    }, timeoutInMillis);
  });

  try {
    // Race the original promise against the timeout - whichever completes first wins
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    // Always clean up the timeout to prevent memory leaks, even if promise rejects
    clearTimeout(timeoutHandle!);
  }
};
